// `.js`, not `.ts`: api/planet/* loads this module at runtime, and Vercel's bundler cannot resolve `.ts`.
import { parseBoothLotId, parsePitchLotId } from '../planet/data/openLots.js';

/**
 * skinContract — the shape both sides of a landmark skin agree on (ADR-0308).
 *
 * A skin is one square PNG a player tiles onto one curated landmark. It lives in the
 * private forge bucket and is served to its owner through a one-hour signed URL, like a
 * forged body. Shared by the API (`api/planet/skins*`) and the planet (`data/userSkins`).
 *
 * ADR-0337: the same PNG may hang on an open lot (`pitch:` / `booth:`) so Game Worlds
 * and ordinary bowls have a creations subject. `cell:` stays a local overlay, never a subject.
 */

/** The tile the client bakes and uploads, pixels per side. */
export const SKIN_PX = 256;
/** The largest square the server accepts, pixels per side. */
export const SKIN_MAX_PX = 512;
/** Upload ceiling, bytes — about a 512² PNG; the brief's "≈ 300 KB per skin". */
export const SKIN_MAX_BYTES = 300 * 1024;
/** Curated landmark ids only — a skin cannot target an arbitrary string. */
export const LANDMARK_ID_PATTERN = /^lm-[a-z0-9-]{1,64}$/;

/**
 * A PLOT is a real building the survey put on EMPTY land (ADR-0327 wave 2): a spot on an HD
 * planet where the bake dropped the building for capacity, and a player asked the Lands
 * Department's model to stand there. Its subject is the building's own ground centroid to
 * 1e-5° (≈ 1 m), integer-encoded because `planet_creations.subject` allows only
 * `[a-z0-9:_-]` — `plot:11415734_2228123` is lon 114.15734, lat 22.28123; a leading `n` is a
 * minus sign. Two taps on the same building land on the same subject, so one player keeps
 * one row per building and the one-public-per-subject rule holds for plots as for landmarks.
 */
export const PLOT_ID_PATTERN = /^plot:n?\d{1,8}_n?\d{1,7}$/;
/**
 * Open-lot subjects (ADR-0336 / 0337). The alphabet is the existing `openLots.ts`
 * parsers — `parsePitchLotId` / `parseBoothLotId` — not a second table.
 * Standing cells (`cell:`) stay local paint discs and must never match.
 */
export const PITCH_SUBJECT_PATTERN = /^pitch:[a-z0-9-]+:\d+$/;
export const BOOTH_SUBJECT_PATTERN = /^booth:[a-z0-9-]+:[a-z0-9-]+$/;
/**
 * A LOOK-room picture frame (ADR-0345). Same private-skin + ju publish-window road as a
 * tower poster (ADR-0308 / 0318), but the subject is the frame — never the landmark mesh.
 * `frame:<roomId>:<frameId>` stays inside `planet_creations.subject`'s 96-char alphabet.
 */
export const FRAME_SUBJECT_PATTERN = /^frame:([a-z0-9-]{1,64}):([a-z0-9-]{1,16})$/;
/** Anything a dressing (skin, poster, building) may hang on: a landmark, a plot, an open lot, a LOOK-room frame, or one planet's seam walls. */
export const SUBJECT_ID_PATTERN =
  /^(lm-[a-z0-9-]{1,64}|plot:n?\d{1,8}_n?\d{1,7}|pitch:[a-z0-9-]+:\d+|booth:[a-z0-9-]+:[a-z0-9-]+|frame:[a-z0-9-]{1,64}:[a-z0-9-]{1,16}|seam:[a-z0-9-]{1,64})$/;
/** One planet's seam walls. `seam:belt` is the older shared subject. */
export function seamSubject(districtId: string): string | null {
  if (!/^[a-z0-9-]{1,64}$/.test(districtId)) return null;
  const id = `seam:${districtId}`;
  return id.length <= 96 ? id : null;
}
export function isSeamSubjectId(id: string): boolean {
  return /^seam:[a-z0-9-]{1,64}$/.test(id);
}
export function isPlotId(id: string): boolean {
  return PLOT_ID_PATTERN.test(id);
}
/** A pitch or carnival booth the player can skin/publish. Never a `cell:`. Never a LOOK frame. */
export function isLotSubjectId(id: string): boolean {
  return parsePitchLotId(id) !== null || parseBoothLotId(id) !== null;
}
export function isFrameSubjectId(id: string): boolean {
  return FRAME_SUBJECT_PATTERN.test(id);
}
export function frameSubject(roomId: string, frameId: string): string {
  return `frame:${roomId}:${frameId}`;
}
export function parseFrameSubject(id: string): { readonly roomId: string; readonly frameId: string } | null {
  const m = FRAME_SUBJECT_PATTERN.exec(id);
  if (!m) return null;
  const roomId = m[1];
  const frameId = m[2];
  if (!roomId || !frameId) return null;
  return { roomId, frameId };
}
export function plotId(lon: number, lat: number): string {
  const enc = (v: number): string => {
    const n = Math.round(Math.abs(v) * 1e5);
    return `${v < 0 && n > 0 ? 'n' : ''}${n}`;
  };
  return `plot:${enc(lon)}_${enc(lat)}`;
}
export function plotCoords(id: string): { readonly lon: number; readonly lat: number } | null {
  const m = /^plot:(n?)(\d{1,8})_(n?)(\d{1,7})$/.exec(id);
  if (!m) return null;
  const lon = (m[1] ? -1 : 1) * (Number(m[2]) / 1e5);
  const lat = (m[3] ? -1 : 1) * (Number(m[4]) / 1e5);
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return null;
  return { lon, lat };
}
export const SKIN_DATA_URL_PREFIX = 'data:image/png;base64,';
/** Most distinct landmarks one player may keep a skin on. Bounds the shared private bucket:
 *  the landmark id is an open pattern, so without this one account could create objects without end. */
export const SKIN_MAX_PER_USER = 50;
/** Skin uploads allowed per minute per player (burst guard; the quota is the real ceiling). */
export const SKIN_UPLOADS_PER_MINUTE = 6;
/** Creations recorded or published per minute per player — bounds the human moderation queue. */
export const CREATION_WRITES_PER_MINUTE = 20;

/**
 * Two ways to wear a picture (ADR-0318 S2). A TILE is the ADR-0308 square, repeated over
 * every wall at one of two scales. A POSTER is the whole picture, any aspect, cropped to fill
 * every face of the tower (amended 2026-09-12) — the primary mode. Both are one PNG in the same bucket.
 */
export type SkinMode = 'tile' | 'poster';
export const SKIN_MODES: readonly SkinMode[] = ['tile', 'poster'];
export function isSkinMode(v: unknown): v is SkinMode {
  return v === 'tile' || v === 'poster';
}
/** A tile is a storey and a half on a belt tower, or five times that. */
export type TileScale = 1 | 5;
export const TILE_SCALES: readonly TileScale[] = [1, 5];
export function isTileScale(v: unknown): v is TileScale {
  return v === 1 || v === 5;
}
/** A poster's long side, pixels; the short side must be at least the tile minimum. */
export const POSTER_MAX_LONG_PX = 1024;
export const POSTER_MIN_SHORT_PX = 64;
/** Poster upload ceiling, bytes — the `bytes` CHECK on planet_creations moved to match. */
export const POSTER_MAX_BYTES = 1024 * 1024;
/**
 * The long side, in px, a PUBLIC poster — someone else's, dressed on your planet — is drawn at.
 * Your own keeps its full POSTER_MAX_LONG_PX: you chose it and look at it closely. Every other one is
 * decoded, then drawn down to this before it reaches the GPU, so a planet dressed in other people's
 * posters costs a quarter of the texture memory and upload of full-size ones (ADR-0318 S2).
 */
export const PUBLIC_POSTER_MAX_PX = 512;

export interface SkinRecord {
  readonly landmarkId: string;
  /** Signed URL for the PNG, valid until `expiresAt`. */
  readonly url: string;
  readonly expiresAt: string;
  /** Absent on a record written before modes existed: a tile at scale 1. */
  readonly mode?: SkinMode;
  readonly scale?: TileScale;
  /** A seam-wall GIF or MP4, signed like the poster. Absent on a still. */
  readonly motion?: 'gif' | 'video';
  readonly motionUrl?: string;
  /** Signed PPT slides 2–4 (poster is `url`). Absent on a single still. */
  readonly reelUrls?: readonly string[];
}
